import bowser from "bowser"

import { useConfigurationStore } from "@/store/configuration.js"
import { useAppStateStore } from "@/store/appState.js"

import Cookies from "js-cookie"

import { isSupported } from "firebase/messaging"

import { eventBus } from "@/plugins/eventBus.js"

import i18n from "@/plugins/i18n"

import * as sentryReporter from "@/services/sentryReporter.js"

const crawlersList = [
  "googlebot",
  "yahoo",
  "bingbot",
  "baiduspider",
  "facebookexternalhit",
  "twitterbot",
  "rogerbot",
  "linkedinbot",
  "embedly",
  "bufferbot",
  "quora link preview",
  "showyoubot",
  "outbrain",
  "pinterest/0.",
  "developers.google.com/+/web/snippet",
  "www.google.com/webmasters/tools/richsnippets",
  "slackbot",
  "vkShare",
  "W3C_Validator",
  "redditbot",
  "Applebot",
  "WhatsApp",
  "flipboard",
  "tumblr",
  "bitlybot",
  "SkypeUriPreview",
  "nuzzel",
  "Discordbot",
  "Google Page Speed",
  "Qwantify",
  "Chrome-Lighthouse"
]

const browser = bowser.getParser(window.navigator.userAgent)
const browserInfo = browser.getBrowser()

let pushNotificationsSupportedByPlatform

export const isIos = function () {
  return browser.parsedResult.os.name === "iOS"
}

export const isBrave = function () {
  return navigator.brave?.isBrave()
}

export const isMobile = () => {
  return browser.parsedResult.platform.type === "mobile"
}

export const isIPhone = function () {
  return Boolean(navigator.platform) && navigator.platform.includes("iPhone")
}

export const isIPod = function () {
  return Boolean(navigator.platform) && navigator.platform.includes("iPod")
}

export const isIPad = function () {
  return Boolean(navigator.platform) && (navigator.platform.includes("iPad") || (/Macintosh/i.test(navigator.userAgent) && navigator.maxTouchPoints > 1))
}

export const isSafari = function () {
  return browserInfo.name === "Safari"
}

export const isFirefox = function () {
  return browserInfo.name === "Firefox"
}

export const isMobileIosSafari = function () {
  return (isIPhone || isIPod) && isSafari() && browser.parsedResult.platform.type === "mobile"
}

export const isMobileAndroid = function () {
  return browser.parsedResult.os.name === "Android" && browser.parsedResult.platform.type === "mobile"
}

// https://github.com/Disciple-Media/disciple-api/blob/master/lib/rack/prerender.rb
export const isCrawler = function () {
  return crawlersList.some((c) => window.navigator.userAgent.toLowerCase().includes(c.toLowerCase()))
}

export const isOrientationLandscape = function () {
  const angle = screen.orientation != null ? screen.orientation.angle : window.orientation
  return Math.abs(angle) === 90
}

export const supportsPushNotifications = function () {
  const appStateStore = useAppStateStore()
  return pushNotificationsSupportedByPlatform && !appStateStore.webViewMode
}

export const getDeviceSummary = function () {
  return `${browser.parsedResult.os.name} ${browser.parsedResult.browser.name} ${browser.parsedResult.browser.version} ${browser.parsedResult.platform.type}`
}

export const setup = async function () {
  pushNotificationsSupportedByPlatform = await isSupported()
  scrollRestorationPolyfillSetup()
  await setupServiceWorker()
}

export const getRevision = function () {
  try {
    const scriptTags = document.getElementsByTagName("script")
    const scriptWithCommitSHA = Array.from(scriptTags).find((s) => s.src.includes("pwa.disciplemedia.com"))
    if (!scriptWithCommitSHA) {
      return "unknown"
    }
    const url = new URL(scriptWithCommitSHA.src)
    const commitSHA = url.pathname.split("/")[1]
    return commitSHA
  } catch (err) {
    sentryReporter.reportError(err)
    return "unknown"
  }
}

export const notifyIfAppVersionOutdated = () => {
  if (import.meta.env.VITE_APP_E2E || import.meta.env.DEV) {
    return
  }

  const configurationStore = useConfigurationStore()
  const usedRevision = getRevision()
  if (usedRevision === configurationStore.configuration.pwaRef) {
    return
  }

  const cookiesRevision = Cookies.get("pwa_url")
  if (cookiesRevision) {
    eventBus.$emit("notify", { message: i18n.global.t("Environment.anUpdateOfThisSiteIsAvailableCookies"), timeout: 30000 })
  } else {
    eventBus.$emit("notify", {
      message: i18n.global.t("Environment.anUpdateOfThisSiteIsAvailable"),
      timeout: 30000,
      customButton: {
        clickCallback: () => window.location.reload(),
        label: i18n.global.t("Environment.refresh")
      }
    })
  }
}

const scrollRestorationPolyfillSetup = function () {
  if ("scrollRestoration" in window.history) {
    window.history.scrollRestoration = "manual"
  }
}

const setupServiceWorker = async function () {
  // Some browsers, e.g. Firefox in private mode won't have navigator.serviceWorker
  if (!navigator.serviceWorker) {
    return
  }

  try {
    const configurationStore = useConfigurationStore()
    const firebaseConfig = configurationStore.configuration.notifications?.firebase_config
    if (pushNotificationsSupportedByPlatform && firebaseConfig) {
      const apiKey = encodeURIComponent(firebaseConfig.apiKey)
      const appId = encodeURIComponent(firebaseConfig.appId)
      const messagingSenderId = encodeURIComponent(firebaseConfig.messagingSenderId)
      const projectId = encodeURIComponent(firebaseConfig.projectId)
      await navigator.serviceWorker.register(
        `/pwa_service_workers/firebase-messaging-sw.js?apiKey=${apiKey}&appId=${appId}&messagingSenderId=${messagingSenderId}&projectId=${projectId}`,
        { scope: "/" }
      )
    } else {
      await navigator.serviceWorker.register("/pwa_service_workers/sw.js", { scope: "/" })
    }
  } catch (err) {
    // https://bugzilla.mozilla.org/show_bug.cgi?id=1413615
    if (err.message === "Rejected") {
      return
    }
  }
}

export const getEnvironment = () => {
  return browser.parsedResult
}

const installPrompt = {}

const beforeInstallPromptListener = (event) => {
  event.preventDefault()

  if (installPrompt.processed) {
    return false
  }

  installPrompt.defferredPrompt = event
  document.addEventListener("click", handleInstallPrompt)
}

export const setInstallPrompt = function () {
  window.addEventListener("beforeinstallprompt", beforeInstallPromptListener)
}

// So unit test can clear data
export const destroy = function () {
  installPrompt.defferredPrompt = null
  installPrompt.processed = false
  window.removeEventListener("beforeinstallprompt", beforeInstallPromptListener)
}

const handleInstallPrompt = async function () {
  document.removeEventListener("click", handleInstallPrompt)

  const appStateStore = useAppStateStore()
  const configurationStore = useConfigurationStore()

  const configuration = configurationStore.configuration
  const installPopupDisabled = appStateStore.installPopupDisabled

  const showPrompt = isMobile() && !configuration.appStoreUrl && !configuration.googlePlayUrl && !installPopupDisabled

  if (installPrompt.defferredPrompt) {
    installPrompt.processed = true
    if (showPrompt) {
      installPrompt.defferredPrompt.prompt()
      const { outcome } = await installPrompt.defferredPrompt.userChoice
      if (outcome === "dismissed") {
        appStateStore.setInstallPopupDisabled()
      }
    }

    installPrompt.defferredPrompt = null
  }
}
